import type { IArticleConfig } from '~/config/article';
import widgetsConfig from '~/config/widgets.config';
import { debugLog, vdebugLog } from '~/debug';
import Session from '~/modules/Session';
import { waitForElement } from '~/utils/dom';
import { safeLocalStorageGet } from '~/utils/localStorage';
import AdvertisingCreator from './index';
import {
  applyDivInsertionStrategy,
  emptyTheContentsOfAnElement,
  findSlotById,
  getTheGPTNumberOfTheAdTarget,
  matchesPath,
} from './AdUnitUtils';
import PrebidAdvertisementUnit from './PrebidAdvertisementUnit';
import type { IAdvancedConfig, IAdTarget } from './types';
import { generateAnArrayOfNumbers, roundToTwo } from '~/utils/general';

/**
 * This class handles the insertion of ad unit divs on the page
 */
class AdUnitCreator {
  /** Can or can not create ads inside the Traffic Exchange widget */
  public static canCreateTXAds: boolean;
  /** Number of articles the ads were generated for */
  public static articleCounter: number = 0;
  /** Advertisement IDs of currently configured ad IDs */
  private currentAdIds: string[] = [];

  constructor(
    private readonly apiKeyConfig: IAdvancedConfig,
    private readonly articleConfig: IArticleConfig
  ) {
    AdUnitCreator.articleCounter++;
  }

  /**
   * Get the place where we should insert the ad unit. Usually used for endless mode
   * @param selector
   * @param target
   * @param windowContext
   * @returns
   */
  private getLocation = (selector: string, target: string, windowContext: Window = window): HTMLElement | null => {
    const match = windowContext.document.querySelectorAll<HTMLElement>(selector)[AdUnitCreator.articleCounter - 1];
    // When in endless mode, we want to select the nth match of the selector,
    // if we're generating ads for 3rd article we should pick the selector match at index of 2
    if (!match && AdUnitCreator.articleCounter - 1 !== 0) {
      /**
       * There can be situations where for example out of 5 articles, we can have less than 5 matches on querySelectorAll
       * With this loop we're checking previous querySelectorAll matches and selecting them
       * while making sure that there was no ad generated already
       */
      for (let i = AdUnitCreator.articleCounter - 1; i > 0; i--) {
        let element = windowContext.document.querySelectorAll<HTMLElement>(selector)[i];
        if (element && !element.parentElement?.querySelector(`div[id*=${target}]`)) {
          return element;
        }
      }
    } else {
      return match;
    }
    return null;
  };

  /**
   * Determine if the element id is the id of the Traffic Exchange widget
   * @param id
   * @returns
   */
  private isTrafficExchange(id: string) {
    return id.includes('vuukle-traffic-exchange-ad');
  }

  /**
   * Get the insert location of the ad based on the selector
   * @param mobileSelector
   * @param selector
   * @param target
   * @param windowContext
   * @returns
   */
  private async getInsertLocationForAnAd(
    mobileSelector: IAdTarget['mobileSelector'],
    selector: IAdTarget['selector'],
    target: string,
    windowContext: Window = window
  ): Promise<HTMLElement | null> {
    let insertLocation: HTMLElement | null = null;
    // Choose either the selector for mobile devices if on mobile or the main selector
    const chosenSelector = Session.isMobileUser && mobileSelector ? mobileSelector : selector;

    if (typeof chosenSelector === 'string') {
      /**
       * Platform often loads before TX which causes the ad divs inside TX to not be found
       * From here on we're going to wait until the div is available and then create the ad unit
       */
      const canCreateTXAdNow =
        typeof AdUnitCreator.canCreateTXAds === 'boolean' && AdUnitCreator.canCreateTXAds === true;

      if (this.isTrafficExchange(chosenSelector) && !canCreateTXAdNow) {
        return null;
      }

      insertLocation = this.articleConfig.endlessMode
        ? this.getLocation(chosenSelector, target)
        : windowContext.document.querySelector(chosenSelector);

      if (!insertLocation) {
        insertLocation = await waitForElement(
          chosenSelector,
          this.apiKeyConfig.adWaitTimeInterval || 250,
          this.apiKeyConfig.numberOfChecksForAdTargets || 40,
          windowContext
        );
      }
    }

    // in case the selector is a javascript expression we will evaluate it and hope an element is returned
    if (typeof chosenSelector === 'function') {
      insertLocation = chosenSelector();
    }

    // in case of a selector array, iterate over it and go with the first one that matches
    if (Array.isArray(chosenSelector)) {
      for (const element of chosenSelector) {
        const location = this.articleConfig.endlessMode
          ? this.getLocation(element, target)
          : windowContext.document.querySelector<HTMLElement>(element);
        if (location) {
          insertLocation = location;
          break;
        }
      }
    }

    return insertLocation;
  }

  /**
   * Apply custom CSS based on user device and configuration
   * @param targetName
   * @param placementElement
   */
  private applyCustomCSS(targetName: string, placementElement: HTMLDivElement | null) {
    if (!placementElement) {
      return;
    }
    const isMobile = Session.isMobileUser;
    // After device detection choose either between mobile specific styles or desktop specific styles.
    // In case they're not present - choose the device independent styles
    const customCSS =
      (isMobile ? this.apiKeyConfig.customMobileCSS?.[targetName] : this.apiKeyConfig.customDesktopCSS?.[targetName]) ||
      this.apiKeyConfig.customCSS?.[targetName];
    if (customCSS) {
      placementElement.style.cssText += customCSS;
    }
  }

  /**
   * Apply grey background to the ad div
   * @param element
   * @param createdAdUnit
   */
  private applyGreyBackground(element: HTMLDivElement, createdAdUnit: PrebidAdvertisementUnit | undefined) {
    element.style.backgroundColor = '#f3f3f3';
    // Add padding to grey box ad units
    if (createdAdUnit) {
      createdAdUnit.wrapper.style.padding = '15px 0';
    }
  }

  /**
   * Insert Traffic Exchange widget on the page
   * @param insertStrategy
   * @param selector
   * @param index
   */
  public insertTXInsideElement(
    insertStrategy: 'inside' | 'before' | 'after' | 'prependInside' | undefined,
    selector: string | (() => any),
    index: string
  ) {
    let insertLocation;
    // in case the selector is a string we will try to select the element
    if (typeof selector === 'string') {
      insertLocation = document.querySelector(selector as string);
    }

    // in case the selector is a javascript expression we will evaluate it and hope an element is returned
    if (typeof selector === 'function') {
      insertLocation = selector();
    }

    // if the selector did yield us an element, create the TX div, insert it at the specified location
    // according to the insertion strategy and generate TX widget
    if (insertLocation && insertLocation instanceof HTMLElement) {
      applyDivInsertionStrategy(
        widgetsConfig.selectors.trafficExchange.replace('#', ''),
        index,
        insertStrategy,
        insertLocation
      );
    }
  }

  /**
   * Create the first ad at it's default location
   * @param placementElement
   * @param gptId
   */
  private createFirstAd(placementElement: HTMLElement | Element | null, gptId?: string) {
    if (!placementElement) {
      debugLog('warn', `[Platform.js] AD Element is missing`);
      return;
    }

    // No need to create a 2nd ad inside the same div
    if (placementElement.querySelector('.vuukle-ads')) {
      return;
    }

    const adUnit = new PrebidAdvertisementUnit(
      this.articleConfig,
      '',
      this.articleConfig.endlessMode ? undefined : '1',
      gptId,
      this.apiKeyConfig.targets?.default?.hideAdLabel,
      this.apiKeyConfig.hideLabelLogo,
      this.apiKeyConfig.targets?.default?.sideAds || 0,
      this.apiKeyConfig.targets?.default?.scrollingAd
    );
    this.currentAdIds.push(adUnit.advertisementID);
    /**
     *  For scrollingAds we need to make sure they're isolated from their placement element
     *  These kinds of ads usually appear over CW and Emotes and we don't want them to scroll down to CW and Emotes
     *
     *  We also don't want the grey background to apply to the whole widget div
     */
    if (this.apiKeyConfig.targets?.default?.scrollingAd || this.apiKeyConfig.targets?.default?.greyBg) {
      const wrapperDiv = document.createElement('div');
      placementElement.appendChild(wrapperDiv);
      wrapperDiv.appendChild(adUnit.wrapper);
      if (this.apiKeyConfig.targets?.default?.greyBg) {
        this.applyGreyBackground(wrapperDiv, adUnit);
      }
    } else {
      placementElement.appendChild(adUnit.wrapper);
    }

    if (!adUnit.wrapper.style.minHeight) {
      adUnit.wrapper.style.minHeight = '330px';
    }
    if (this.articleConfig.endlessMode) {
      adUnit.initVuukleHBQueue();
    }

    this.applyCustomCSS('default', adUnit.wrapper);

    /** Perform post-ad insertion callback call */
    if (typeof this.apiKeyConfig.targets?.['default']?.postInsertionOperation !== 'undefined') {
      this.apiKeyConfig.targets?.['default']?.postInsertionOperation?.();
    }
  }

  /**
   * Create the second ad at it's default location
   * @param placementElement
   * @param commentsOrEmotesEnabled
   * @param index
   * @param gptId
   */
  private createSecondAd(
    placementElement: HTMLElement | Element | null,
    commentsOrEmotesEnabled: boolean,
    index: string,
    gptId?: string
  ) {
    const secondAdPlacementElement = document.querySelector('#vuukle-ad-2'.concat(index));
    /** Create second AD if comments or emotes are enabled or the custom second ad div is present */
    // No need to create a 2nd ad inside the same div
    if (
      (placementElement && placementElement.querySelectorAll('.vuukle-ads').length === 2) ||
      (secondAdPlacementElement && secondAdPlacementElement.querySelectorAll('.vuukle-ads').length === 2)
    ) {
      return;
    }
    if (commentsOrEmotesEnabled || secondAdPlacementElement) {
      const secondADUnit = new PrebidAdvertisementUnit(
        this.articleConfig,
        '',
        this.articleConfig.endlessMode ? undefined : '2',
        gptId,
        this.apiKeyConfig.targets?.secondDefault?.hideAdLabel,
        this.apiKeyConfig.hideLabelLogo,
        this.apiKeyConfig.targets?.secondDefault?.sideAds || 0,
        this.apiKeyConfig.targets?.secondDefault?.scrollingAd
      );
      this.currentAdIds.push(secondADUnit.advertisementID);
      if (secondAdPlacementElement) {
        secondAdPlacementElement.appendChild(secondADUnit.wrapper);
      } else if (placementElement) {
        /**
         *  For scrollingAds we need to make sure they're isolated from their placement element
         *  These kinds of ads usually appear over CW and Emotes and we don't want them to scroll down to CW and Emotes
         *
         *  We also don't want the grey background to apply to the widget divs as well
         */
        if (this.apiKeyConfig.targets?.secondDefault?.scrollingAd || this.apiKeyConfig.targets?.secondDefault?.greyBg) {
          const wrapperDiv = document.createElement('div');
          placementElement.appendChild(wrapperDiv);
          wrapperDiv.appendChild(secondADUnit.wrapper);
          wrapperDiv.style.order = '2';
          if (this.apiKeyConfig.targets?.secondDefault?.greyBg) {
            this.applyGreyBackground(wrapperDiv, secondADUnit);
          }
        } else {
          placementElement.appendChild(secondADUnit.wrapper);
        }
        secondADUnit.wrapper.style.order = '2';
      } else {
        debugLog('warn', `[Platform.js] Second AD Element is missing`);
        return;
      }

      if (this.articleConfig.endlessMode) {
        secondADUnit.initVuukleHBQueue();
      }
      if (!secondADUnit.wrapper.style.minHeight) {
        secondADUnit.wrapper.style.minHeight = '330px';
      }

      this.applyCustomCSS('secondDefault', secondADUnit.wrapper);

      /** Perform post-ad insertion callback call */
      if (typeof this.apiKeyConfig.targets?.['secondDefault']?.postInsertionOperation !== 'undefined') {
        this.apiKeyConfig.targets?.['secondDefault']?.postInsertionOperation?.();
      }
    }
  }

  /**
   * Create an ad at a targeted location
   * @param placementElement
   * @param targetName
   * @param gptNumber
   * @param gptId
   * @param index
   * @returns
   */
  public createAdAtCustomLocation(
    placementElement: HTMLElement | Element | null,
    targetName: string,
    gptNumber?: string,
    gptId?: string,
    index: string = ''
  ) {
    if (!placementElement) {
      debugLog('warn', `[Platform.js] Custom AD Element is missing`);
      return;
    }

    // No need to create a 2nd ad inside the same div
    if (placementElement.querySelector('.vuukle-ads')) {
      return;
    }
    const adUnit = new PrebidAdvertisementUnit(
      this.articleConfig,
      index,
      gptNumber,
      gptId,
      this.apiKeyConfig.targets?.[targetName]?.hideAdLabel,
      this.apiKeyConfig.hideLabelLogo,
      0, // no side ads for custom targets
      this.apiKeyConfig.targets?.[targetName]?.scrollingAd
    );
    this.currentAdIds.push(adUnit.advertisementID);
    placementElement.appendChild(adUnit.wrapper);
    // Activate interscroller mode with Quiz inside of it
    if (
      (Session.isMobileUser || (!Session.isMobileUser && this.apiKeyConfig.quizzlyInterscroller?.enableOnDesktop)) &&
      this.apiKeyConfig.quizzlyInterscroller?.adTarget &&
      this.apiKeyConfig.quizzlyInterscroller?.adTarget === targetName
    ) {
      const quizElement =
        document.querySelector<HTMLElement>(widgetsConfig.selectors.quiz.concat(index)) ||
        document.querySelector<HTMLElement>(widgetsConfig.selectors.quizAlt.concat(index));
      if (quizElement) {
        adUnit.constructInterscrollerAd(quizElement);
      }
    }
    if (this.articleConfig.endlessMode) {
      adUnit.initVuukleHBQueue();
    }

    const adElementParent = placementElement.parentElement;
    /** TX ads aren't initialized right away, we need to call the refresh function for them */
    if (adElementParent && this.isTrafficExchange(adElementParent.id)) {
      adUnit.refreshAd();
    }

    /** Perform post-ad insertion callback call */
    if (typeof this.apiKeyConfig.targets?.[targetName]?.postInsertionOperation !== 'undefined') {
      this.apiKeyConfig.targets?.[targetName]?.postInsertionOperation?.();
    }

    /** Check if the ad unit is inserted near the quiz widget. If it is, then add a gap between the ad unit and the quiz unit for Desktop screens */
    let nearestQuiz: HTMLDivElement | null = null;
    // If quiz insertion config is already given, we can skip this step because we already add a gap during insertion
    const hasQuizInsertionConfig = typeof this.apiKeyConfig.insertQuizAndAd !== 'undefined';

    if (adUnit.wrapper.nextElementSibling && adUnit.wrapper.nextElementSibling.id.includes('vuukle-quiz')) {
      nearestQuiz = adUnit.wrapper.nextElementSibling as HTMLDivElement;
    }

    if (adUnit.wrapper.previousElementSibling && adUnit.wrapper.previousElementSibling.id.includes('vuukle-quiz')) {
      nearestQuiz = adUnit.wrapper.previousElementSibling as HTMLDivElement;
    }

    if (nearestQuiz && nearestQuiz.parentElement && !hasQuizInsertionConfig) {
      const quizParent = nearestQuiz.parentElement;
      const quizParentContainer = quizParent.parentElement;

      // If the container where quiz and ad are placed is wider than the amount of width needed to create a gap
      // then proceed adding the gap between quiz and ad
      const quizWidth = 300;
      const adWidth = 300;
      const gapWidth = 5;
      const requiredAmountOfWidth = quizWidth + adWidth + gapWidth;

      if (!Session.isMobileUser && quizParentContainer && quizParentContainer.offsetWidth > requiredAmountOfWidth) {
        quizParent.style.gap = `${gapWidth}px`;
      }
    }

    return adUnit;
  }

  /**
   * Insert a custom GPT (Google Publisher Tag) ad at a custom destination
   * @param targetName
   * @param gptId
   * @param placementElement
   * @param index
   * @param gptNumberOverride
   */
  private async createGPTUnit(
    targetName: string,
    placementElement: HTMLDivElement | null,
    gptId: string | undefined,
    index: string,
    gptNumberOverride?: string
  ) {
    const gptNumber: string | undefined = gptNumberOverride
      ? gptNumberOverride.toString()
      : getTheGPTNumberOfTheAdTarget(targetName);
    const createdAdUnit = this.createAdAtCustomLocation(
      placementElement,
      targetName,
      Session.isMobileUser && this.apiKeyConfig.targets?.[targetName]?.gptNumberMobile
        ? this.apiKeyConfig.targets?.[targetName]?.gptNumberMobile
        : gptNumber,
      gptId,
      index
    );
    // Add grey bg
    if (this.apiKeyConfig.targets?.[targetName]?.greyBg && placementElement) {
      this.applyGreyBackground(placementElement, createdAdUnit);
    }
  }

  /**
   * Generate ads that appear either at the left or right side of the screen
   * @param placementElement
   * @param targetName
   */
  private generateVerticalSideAds(placementElement: HTMLElement | null, targetName: string) {
    /**
     * Make ads appear at left or right hand side, fixed to the screen, at 160x600 size
     * @param direction
     * @returns
     */
    const getCSSForSideAd = (direction: 'left' | 'right') =>
      `
       position: fixed;
       z-index: 3000;
       ${direction}: 0px;
       margin-left: 5px;
       top: 30px;
       width: 160px;
       height: 600px;
       display: block;
       text-align: center;
       margin-top: 50px;
     `;

    if (this.apiKeyConfig.targets?.[targetName].enableLeftVertical && placementElement) {
      placementElement.style.cssText += getCSSForSideAd('left');
    } else if (this.apiKeyConfig.targets?.[targetName].enableRightVertical && placementElement) {
      placementElement.style.cssText += getCSSForSideAd('right');
    }
  }

  /**
   * Insert a custom GPT or passback ad
   * @param targetName
   * @param gptId
   * @param placementElement
   * @param index
   */
  private async createCustomAd(
    targetName: string,
    gptId: string | undefined,
    placementElement: HTMLDivElement | null,
    index: string = ''
  ) {
    if (this.apiKeyConfig.targets?.[targetName].passbackUnit) {
      const passbackAdUnit = await import(/* webpackChunkName: 'pba' */ './PassbackAdUnit');
      new passbackAdUnit.default(this.apiKeyConfig, placementElement, targetName);
      if (this.apiKeyConfig.targets?.[targetName].passbackUnit?.replaceWithGPT) {
        const replaceTimer = (this.apiKeyConfig.targets?.[targetName]?.passbackUnit?.replaceTime || 30) * 1000;
        setTimeout(() => {
          if (placementElement) {
            emptyTheContentsOfAnElement(placementElement);
          }
          this.createGPTUnit(
            targetName,
            placementElement,
            gptId,
            index,
            this.apiKeyConfig.targets?.[targetName]?.gptNumber
          );
        }, replaceTimer);
      }
    } else if (this.apiKeyConfig.targets?.[targetName].outbrainUnit) {
      const outbrainAdUnit = await import(/* webpackChunkName: 'oba' */ './OutbrainAdUnit');
      new outbrainAdUnit.default(this.apiKeyConfig.targets[targetName].outbrainUnit, placementElement);
    } else {
      this.createGPTUnit(
        targetName,
        placementElement,
        gptId,
        index,
        this.apiKeyConfig.targets?.[targetName]?.gptNumber
      );
    }

    this.generateVerticalSideAds(placementElement, targetName);
    this.applyCustomCSS(targetName, placementElement);
  }

  /**
   * Create and insert an ad unit div inside a chosen element
   * @param index
   * @param target
   */
  private async insertAdDivAtGivenSelector(index: string, target: string) {
    if (this.apiKeyConfig.targets) {
      const currentTarget = this.apiKeyConfig.targets[target];
      const { selector, mobileSelector, insert, gptId, gptIdMobile } = currentTarget;
      const insertLocation = await this.getInsertLocationForAnAd(mobileSelector, selector, target);

      // if the selector did yield us an element, create the ad div, insert it at the specified location
      // according to the insertion strategy and generate an ad
      const isAdAlreadyPresent = document.getElementById(target.concat(index));
      if (insertLocation && !isAdAlreadyPresent) {
        const adDiv = applyDivInsertionStrategy(target, index, insert, insertLocation);

        // Skip ad creation. Usually used when we just need a div and an external (ad) script is attaching to that div
        if (this.apiKeyConfig.targets?.[target].skipAdCreation) {
          return;
        }
        this.createCustomAd(target, Session.isMobileUser && gptIdMobile ? gptIdMobile : gptId, adDiv, index);
      } else {
        const chosenSelector = Session.isMobileUser && mobileSelector ? mobileSelector : selector;
        // The element is absent, log a message
        debugLog(
          'warn',
          typeof chosenSelector === 'string'
            ? `[Platform.js] Element at the selector '${chosenSelector}' is missing`
            : `[Platform.js] Element at the evaluated selector is missing`
        );
      }
    }
  }

  /**
   * Sometimes we may want to not insert an ad dynamically using selector and insert before a specific date.
   * This can happen when a publisher for example chooses to insert the ad divs themselves after a certain date and they're not
   * able to update their previous articles before that date to have manually inserted ad divs because they have too many articles
   * to update.
   * @param index
   * @param target
   * @param injectionBypass
   */
  public decideWhetherAdShouldBeDynamicallyInserted(index: string, target: string, injectionBypass: boolean) {
    if (
      typeof this.apiKeyConfig.getDateFunction === 'function' &&
      this.apiKeyConfig.injectAdsDynamicallyBeforeDate &&
      !injectionBypass
    ) {
      const date = this.apiKeyConfig.getDateFunction();

      if (!(date instanceof Date)) {
        this.insertAdDivAtGivenSelector(index, target);
        return;
      }

      const beforeDate = new Date(this.apiKeyConfig.injectAdsDynamicallyBeforeDate);

      if (date.valueOf() < beforeDate.valueOf()) {
        this.insertAdDivAtGivenSelector(index, target);
      }
    } else {
      this.insertAdDivAtGivenSelector(index, target);
    }
  }

  /**
   * Inserts ad(s) on the page using configuration
   * @param wrapperForAD
   * @param commentsOrEmotesEnabled
   * @param index
   */
  public async createAdvancedAd(wrapperForAD: HTMLDivElement | null, commentsOrEmotesEnabled: boolean, index: string) {
    if (this.apiKeyConfig.targets) {
      Object.keys(this.apiKeyConfig.targets).forEach(async (target) => {
        if (
          this.apiKeyConfig.targets?.[target].adUnitExcludedGeos &&
          this.apiKeyConfig.targets?.[target].adUnitExcludedGeos.includes(Session.userCountry)
        ) {
          // If the ad is excluded from the current user geo - return from function
          return;
        }
        if (
          this.apiKeyConfig.targets?.[target].adUnitGeos &&
          !this.apiKeyConfig.targets?.[target].adUnitGeos!.includes(Session.userCountry)
        ) {
          // If the ad is geo exclusive and current user geo doesn't match - return from function
          return;
        }
        // If the target is excluded from certain paths in the website, we should prevent the ad unit from being created
        if (this.apiKeyConfig.targets?.[target].ignorePaths || this.apiKeyConfig.targets?.[target].ignorePathsMobile) {
          const ignorePaths =
            Session.isMobileUser && this.apiKeyConfig.targets?.[target].ignorePathsMobile
              ? this.apiKeyConfig.targets?.[target].ignorePathsMobile
              : this.apiKeyConfig.targets?.[target].ignorePaths || [];
          const doesPathMatch = matchesPath(ignorePaths as string[]);
          if (doesPathMatch) {
            return;
          }
        }
        // Prevent ad unit from being created if restricted to either Desktop or Mobile
        if (typeof this.apiKeyConfig.targets?.[target].onlyDesktop === 'boolean') {
          const onMobileDesktopOnly = Session.isMobileUser && this.apiKeyConfig.targets?.[target].onlyDesktop;
          const onDesktopMobileOnly = !Session.isMobileUser && !this.apiKeyConfig.targets?.[target].onlyDesktop;
          if (onMobileDesktopOnly || onDesktopMobileOnly) {
            return;
          }
        }
        // Perform an operation before inserting the ad unit
        if (typeof this.apiKeyConfig.targets?.[target].preInsertionOperation === 'function') {
          this.apiKeyConfig.targets[target].preInsertionOperation?.();
        }
        // default and secondDefault are the default locations for the first and second ads and these cases need to be handled differently
        // default and secondDefault can also be prevented from creation
        const noDefaultAds = typeof this.articleConfig.ads === 'object' && this.articleConfig.ads.noDefaults;
        if ((target === 'default' || target === 'secondDefault') && !noDefaultAds) {
          target === 'default'
            ? this.createFirstAd(
                wrapperForAD,
                Session.isMobileUser && this.apiKeyConfig.targets?.default?.gptIdMobile
                  ? this.apiKeyConfig.targets?.default?.gptIdMobile
                  : this.apiKeyConfig.targets?.default?.gptId
              )
            : this.createSecondAd(
                wrapperForAD,
                commentsOrEmotesEnabled,
                index,
                Session.isMobileUser && this.apiKeyConfig.targets?.default?.gptIdMobile
                  ? this.apiKeyConfig.targets?.secondDefault?.gptIdMobile
                  : this.apiKeyConfig.targets?.secondDefault?.gptId
              );
        } else {
          const requiresManualInsertion =
            this.apiKeyConfig.targets?.[target]?.insert &&
            (this.apiKeyConfig.targets[target].selector || this.apiKeyConfig.targets[target].mobileSelector);
          if (requiresManualInsertion) {
            this.decideWhetherAdShouldBeDynamicallyInserted(
              index,
              target,
              Boolean(this.apiKeyConfig.targets?.[target].bypassDynamicInjectionDateLimit)
            );
          } else {
            // Skip ad creation. Usually used when we just need a div and an external (ad) script is attaching to that div
            if (this.apiKeyConfig.targets?.[target].skipAdCreation) {
              return;
            }

            // if no insertion tactic is specified, we assume the element is inserted inside the given target div
            // already provided by the publisher
            let targetElement: HTMLDivElement | null = document.getElementById(target.concat(index)) as HTMLDivElement;
            /** Give 20 chances for the element to show up in 250ms intervals */
            if (!targetElement) {
              targetElement = (await waitForElement(`#${target.concat(index)}`, 250, 20)) as any;
            }

            this.createCustomAd(
              target,
              Session.isMobileUser && this.apiKeyConfig.targets?.[target].gptIdMobile
                ? this.apiKeyConfig.targets[target].gptIdMobile
                : this.apiKeyConfig.targets?.[target].gptId,
              targetElement,
              index
            );
          }
        }
      });
    }
  }

  /**
   * Replace Vuukle or non-Vuukle ads with other Vuukle ads
   * @param adsToReplace The ad div IDs that will be replaced
   * @param adReplacementMap The map of ad divs that are going to be replaced -> the ad div that's replacing them
   * @param adInViewRefreshPercentages The map of how much of the ad should be visible in percentages before it gets replaced
   * @param index Article index
   */
  public replaceAdsWhenInView(
    adsToReplace: string[],
    adReplacementMap: Record<string, string>,
    adInViewRefreshPercentages: Record<string, number>,
    index: string
  ) {
    const adsWithIObserver: string[] = [];
    const adIObservers: Record<string, IntersectionObserver> = {};
    const adVisibility: Record<string, number> = {};
    const adsAlreadyUsedForReplacement: string[] = [];
    const adsThatHaveReplacedExistingAds: string[] = [];

    const attachObservers = () => {
      const createObserverForAd = (ad: string) => {
        if (adsWithIObserver.includes(ad)) {
          return;
        }
        const theAdDiv = document.querySelector(`div[id="${ad}"]`);
        if (theAdDiv) {
          const intObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
              entries.forEach((entry) => {
                const { intersectionRatio } = entry;
                adVisibility[ad] = roundToTwo(intersectionRatio * 100);
                vdebugLog('log', true, `[Platform.js] Current visibility of ${ad}: ${adVisibility[ad]}%`);
              });
            },
            { threshold: generateAnArrayOfNumbers(0, 1, 0.05) }
          );
          intObserver.observe(theAdDiv);
          adsWithIObserver.push(ad);
          adIObservers[ad] = intObserver;
        }
      };
      adsToReplace.forEach((ad) => createObserverForAd(ad));
      adsThatHaveReplacedExistingAds.forEach((ad) => createObserverForAd(ad));
    };

    setInterval(() => {
      attachObservers();
    }, 5000);

    setInterval(() => {
      const performAdReplacement = (ad: string) => {
        if (adVisibility[ad] >= adInViewRefreshPercentages[ad]) {
          adIObservers[ad]?.disconnect();
          if (adsWithIObserver.indexOf(ad) > -1) {
            adsWithIObserver.splice(adsWithIObserver.indexOf(ad), 1);
          }

          const adTargetThatReplacesThisAd = adReplacementMap[ad];
          if (!adsAlreadyUsedForReplacement.includes(ad)) {
            adsAlreadyUsedForReplacement.push(ad);
            adsAlreadyUsedForReplacement.push(adTargetThatReplacesThisAd);
          }

          const replacementDiv = document.createElement('div');
          replacementDiv.id = adTargetThatReplacesThisAd;

          if (!adsThatHaveReplacedExistingAds.includes(ad)) {
            adsThatHaveReplacedExistingAds.push(adTargetThatReplacesThisAd);
          }

          const adThatIsBeingReplaced = document.querySelector(`div[id="${ad}"]`);
          if (adThatIsBeingReplaced) {
            adThatIsBeingReplaced.replaceWith(replacementDiv);
            const adUnit = this.createAdAtCustomLocation(
              replacementDiv,
              adTargetThatReplacesThisAd,
              Session.isMobileUser && this.apiKeyConfig.targets?.[adTargetThatReplacesThisAd]?.gptNumberMobile
                ? this.apiKeyConfig.targets?.[adTargetThatReplacesThisAd]?.gptNumberMobile
                : getTheGPTNumberOfTheAdTarget(adTargetThatReplacesThisAd),
              Session.isMobileUser && this.apiKeyConfig.targets?.[adTargetThatReplacesThisAd].gptIdMobile
                ? this.apiKeyConfig.targets[adTargetThatReplacesThisAd].gptIdMobile
                : this.apiKeyConfig.targets?.[adTargetThatReplacesThisAd].gptId,
              index
            );

            if (adUnit) {
              adUnit.refreshAd();
            }

            vdebugLog(
              'log',
              true,
              `[Platform.js] ${ad} ad with the visibility of ${adVisibility[ad]}% replaced with ${adTargetThatReplacesThisAd}`
            );
          }
        }

        if (adVisibility[ad] < adInViewRefreshPercentages[ad]) {
          vdebugLog(
            'log',
            true,
            `[Platform.js] ${ad} is not visible enough to the user (Current visibility: ${adVisibility[ad]}%, Required visibility: ${adInViewRefreshPercentages[ad]}%). Skipping replacement...`
          );
        }

        attachObservers();
      };
      adsToReplace.forEach((ad) => {
        if (!adsAlreadyUsedForReplacement.includes(ad)) {
          performAdReplacement(ad);
        }
      });
      adsThatHaveReplacedExistingAds.forEach((ad) => {
        performAdReplacement(ad);
      });
    }, 30000);
  }

  public performCustomAdRefreshes(config: IAdvancedConfig['customAdRefresh'], index: string = '') {
    if (!window.googletag.pubads) {
      setTimeout(() => this.performCustomAdRefreshes(config), 100);
      return;
    }
    const configuration: IAdvancedConfig['customAdRefresh'] = config!;
    const slotVisibility: Record<string, number> = {};
    const slotEmptiness: Record<string, boolean> = {};
    const slotSizes: Record<string, string> = {};
    const adsAlreadyUsedForReplacement: string[] = [];
    const slotsWithIObserver: string[] = [];
    const minimumRequiredViewPercentage: number = configuration.inViewRefreshPercentage || 50;

    /**
     * Watch for slot visibility
     * The 'slotVisibilityChanged' event is not suitable for this task unfortunately because it lags behind the 'slotRenderEnded' event
     */
    const trackAdSlotVisibility = () => {
      const allSlots = window.googletag.pubads().getSlots();
      allSlots.forEach((slot: any) => {
        if (slotsWithIObserver.includes(slot.getSlotElementId())) {
          return;
        }
        const slotParent = document.querySelector(`div[id="${slot.getSlotElementId()}"]`)?.parentElement;
        if (slotParent) {
          const intObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]) => {
              entries.forEach((entry) => {
                const { intersectionRatio } = entry;
                slotVisibility[slot.getSlotElementId()] = roundToTwo(intersectionRatio * 100);
                vdebugLog(
                  'log',
                  true,
                  `[Platform.js] Current visibility of ${slot.getElementById()}: ${
                    slotVisibility[slot.getSlotElementId()]
                  }%`
                );
              });
            },
            { threshold: generateAnArrayOfNumbers(0, 1, 0.05) }
          );
          intObserver.observe(slotParent);
          slotsWithIObserver.push(slot.getSlotElementId());
        }
      });
    };

    window.googletag.cmd.push(() => {
      const allSlots = window.googletag.pubads().getSlots();
      if (allSlots && allSlots.length > 0) {
        trackAdSlotVisibility();
      } else {
        const interval = setInterval(() => {
          const slots = window.googletag.pubads().getSlots();
          if (slots && slots.length > 0) {
            trackAdSlotVisibility();
            clearInterval(interval);
          }
        }, 200);
      }
    });

    setInterval(() => {
      window.googletag.cmd.push(() => {
        trackAdSlotVisibility();
        const allSlots = window.googletag.pubads().getSlots();
        if (allSlots && allSlots.length > 0) {
          allSlots.forEach((slot: any) => {
            const slotLineItemID =
              slot.getResponseInformation()?.lineItemId || slot.getResponseInformation()?.sourceAgnosticLineItemId;
            const slotId = slot.getSlotElementId();
            const slotElement = document.querySelector<HTMLElement>(`div[id="${slotId}"]`)!;
            const size = slotElement
              ? `${slotElement.parentElement!.offsetWidth}x${slotElement.parentElement!.offsetHeight}`
              : '';

            // Ignore Vuukle ads
            if (slotId.includes('1497448474263')) {
              return;
            }

            if (configuration.gamOrderIdExclusion && configuration.gamOrderIdExclusion.includes(slotLineItemID)) {
              vdebugLog(
                'log',
                true,
                `[Platform.js] ${slotId} ad with line item ID of ${slotLineItemID} detected, this ad is excluded from being refreshed. Skipping ad replacement...`
              );
              return;
            }

            const replaceAd = () => {
              const adsForTheCurrentSize = configuration.sizesForTargets[slotSizes[slotId] || size];
              if (adsForTheCurrentSize) {
                for (const ad of adsForTheCurrentSize) {
                  if (adsAlreadyUsedForReplacement.includes(ad)) {
                    continue;
                  }

                  adsAlreadyUsedForReplacement.push(ad);
                  const adDiv = document.createElement('div');
                  adDiv.id = ad;
                  slotElement.replaceWith(adDiv);
                  const adUnit = this.createAdAtCustomLocation(
                    adDiv,
                    ad,
                    Session.isMobileUser && this.apiKeyConfig.targets?.[ad]?.gptNumberMobile
                      ? this.apiKeyConfig.targets?.[ad]?.gptNumberMobile
                      : getTheGPTNumberOfTheAdTarget(ad),
                    Session.isMobileUser && this.apiKeyConfig.targets?.[ad].gptIdMobile
                      ? this.apiKeyConfig.targets[ad].gptIdMobile
                      : this.apiKeyConfig.targets?.[ad].gptId,
                    index
                  );
                  if (adUnit) {
                    adUnit.refreshAd();
                  }
                  vdebugLog('log', true, `[Platform.js] ${slotId} ad replaced with ${ad} of size ${size}`);
                  break;
                }
              }
            };

            if (configuration.gamOrderIdInclusion && configuration.gamOrderIdInclusion.includes(slotLineItemID)) {
              if (configuration.disableViewableRefresh) {
                if (configuration.sizesForTargets[slotSizes[slotId] || size]) {
                  vdebugLog(
                    'log',
                    true,
                    `[Platform.js] Replacing ${slotId} ad with line item ID of ${slotLineItemID} and of ${size} size...`
                  );
                }
                replaceAd();
              } else if (
                typeof slotVisibility[slotId] === 'number' &&
                slotVisibility[slotId] >= minimumRequiredViewPercentage
              ) {
                if (configuration.sizesForTargets[slotSizes[slotId] || size]) {
                  vdebugLog(
                    'log',
                    true,
                    `[Platform.js] Replacing ${slotId} ad with line item ID of ${slotLineItemID} with visibility of ${slotVisibility[slotId]}% of and of ${size} size...`
                  );
                }
                replaceAd();
              }
            }

            const refreshIfEmptyOrNot =
              !configuration.onlyRefreshIfEmpty || (configuration.onlyRefreshIfEmpty && slotEmptiness[slotId]);

            // Replace visible empty slots
            if (refreshIfEmptyOrNot && configuration.disableViewableRefresh) {
              if (configuration.sizesForTargets[slotSizes[slotId] || size]) {
                vdebugLog(
                  'log',
                  true,
                  `[Platform.js] Replacing ${slotId} ${slotEmptiness[slotId] ? 'empty' : ''} ad of ${size} size...`
                );
              }

              replaceAd();
            } else if (
              refreshIfEmptyOrNot &&
              typeof slotVisibility[slotId] === 'number' &&
              slotVisibility[slotId] >= minimumRequiredViewPercentage
            ) {
              if (configuration.sizesForTargets[slotSizes[slotId] || size]) {
                vdebugLog(
                  'log',
                  true,
                  `[Platform.js] Replacing ${slotId} ${slotEmptiness[slotId] ? 'empty' : ''} ad of with visibility of ${
                    slotVisibility[slotId]
                  }% and of ${size} size...`
                );
              }

              replaceAd();
            }
          });
        }
      });
    }, 30000);

    window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
      const slotId = event.slot.getSlotElementId();
      const slotElement = document.querySelector<HTMLElement>(`div[id="${slotId}"]`)!;
      const size = event.size
        ? event.size.join('x')
        : slotElement
          ? `${slotElement.parentElement!.offsetWidth}x${slotElement.parentElement!.offsetHeight}`
          : '';
      const lineItemID = event.lineItemId || event.sourceAgnosticLineItemId;
      const isEmpty = event.isEmpty;

      if (event.size) {
        slotSizes[slotId] = event.size.join('x');
      }

      const details = {
        'Advertiser ID': event.advertiserId,
        'Campaign ID': event.campaignId,
        'Company IDs': event.companyIds,
        'Creative ID': event.creativeId,
        'Creative Template ID': event.creativeId,
        'Is backfill?': event.isBackfill,
        'Is empty?': event.isEmpty,
        'Label IDs': event.labelIds,
        'Line Item ID': event.lineItemId,
        Size: event.size ? event.size.join('x') : size,
        'Slot content changed?': event.slotContentChanged,
        'Source Agnostic Creative ID': event.sourceAgnosticCreativeId,
        'Source Agnostic Line Item ID': event.sourceAgnosticLineItemId,
        'Yield Group IDs': event.yieldGroupIds,
      };

      slotEmptiness[slotId] = isEmpty;

      vdebugLog(
        'log',
        true,
        '[Platform.js] Ad render details',
        slotId,
        details,
        `size: ${size}`,
        `visibility %: ${slotVisibility[slotId]}`,
        `line item ID: ${lineItemID}`,
        `is empty: ${isEmpty}`
      );

      trackAdSlotVisibility();
    });
  }

  /**
   * Replace other ad units on the page with GPT units after 30 seconds
   * @param index
   */
  public findAndReplaceExistingAdUnits(index: string = '') {
    const { targets, replaceAdsAtTopLevel, adUnit, prebid7 } = this.apiKeyConfig;

    const windowContext = replaceAdsAtTopLevel ? (window.top as Window & typeof globalThis) : window;

    if (replaceAdsAtTopLevel) {
      const vuukleConf = JSON.parse(
        safeLocalStorageGet(`vuukleconfhttps://cdn.vuukle.com/ads/${this.articleConfig.host}.json`) || '{}'
      );
      if (vuukleConf && vuukleConf.data) {
        windowContext._vuukleConfig = vuukleConf.data;
      }
      AdvertisingCreator.loadPrebid(adUnit, prebid7, windowContext);
    }

    if (targets) {
      const adReplacers: { [key: string]: IAdTarget } = {};
      Object.keys(targets).forEach((target) => {
        const ignorePaths =
          Session.isMobileUser && targets[target].replaceExistingAd?.ignorePathsMobile
            ? targets[target].replaceExistingAd?.ignorePathsMobile
            : targets[target].replaceExistingAd?.ignorePaths || [];
        if (targets[target].replaceExistingAd && !matchesPath((ignorePaths || []) as string[], windowContext)) {
          adReplacers[target] = targets[target];
          const adReplacementSelector =
            Session.isMobileUser && targets[target].replaceExistingAd?.mobileSelector
              ? targets[target].replaceExistingAd?.mobileSelector
              : targets[target].replaceExistingAd?.selector;
          let gptAdId = '';

          /** Get the GPT Ad ID so that we can later use it in ad GAM whitelist logic below these lines */
          if (typeof adReplacementSelector === 'function') {
            gptAdId = adReplacementSelector().id;
          }

          if (typeof adReplacementSelector === 'string') {
            gptAdId = adReplacementSelector.slice(1);
          }

          if (Array.isArray(adReplacementSelector)) {
            for (const selector of adReplacementSelector) {
              if (document.querySelector(selector)) {
                gptAdId = selector.slice(1);
                break;
              }
            }
          }

          if (gptAdId) {
            this.currentAdIds.push(gptAdId);
          }
        }
      });

      if (Object.keys(adReplacers).length > 0) {
        const replaceAd = async (ad: string) => {
          // If the target is excluded from certain paths in the website, we should prevent the ad unit from being created
          if (this.apiKeyConfig.targets?.[ad].ignorePaths || this.apiKeyConfig.targets?.[ad].ignorePathsMobile) {
            const ignorePaths =
              Session.isMobileUser && this.apiKeyConfig.targets?.[ad].ignorePathsMobile
                ? this.apiKeyConfig.targets?.[ad].ignorePathsMobile
                : this.apiKeyConfig.targets?.[ad].ignorePaths || [];
            const doesPathMatch = matchesPath(ignorePaths as string[], windowContext);
            if (doesPathMatch) {
              return;
            }
          }
          // Prevent ad unit from being created if restricted to either Desktop or Mobile
          if (typeof this.apiKeyConfig.targets?.[ad].onlyDesktop === 'boolean') {
            const onMobileDesktopOnly = Session.isMobileUser && this.apiKeyConfig.targets?.[ad].onlyDesktop;
            const onDesktopMobileOnly = !Session.isMobileUser && !this.apiKeyConfig.targets?.[ad].onlyDesktop;
            if (onMobileDesktopOnly || onDesktopMobileOnly) {
              return;
            }
          }
          const insertLocation = await this.getInsertLocationForAnAd(
            adReplacers[ad].replaceExistingAd?.mobileSelector,
            adReplacers[ad].replaceExistingAd?.selector,
            ad,
            windowContext
          );

          // if the selector did yield us an element, create the ad div, insert it at the specified location
          // according to the insertion strategy and generate an ad
          if (insertLocation && !windowContext.document.getElementById(ad.concat(index))) {
            // Empty the contents of the div before creating an ad inside of it
            emptyTheContentsOfAnElement(insertLocation);

            const adDiv = applyDivInsertionStrategy(
              ad,
              index,
              adReplacers[ad].replaceExistingAd?.insert,
              insertLocation,
              windowContext
            );

            // If there's an override for the ad unit, it should be used ahead of the specified ad unit
            this.createCustomAd(
              ad,
              Session.isMobileUser && this.apiKeyConfig.targets?.[ad]?.gptIdMobile
                ? this.apiKeyConfig.targets?.[ad].gptIdMobile
                : this.apiKeyConfig.targets?.[ad]?.gptId,
              adDiv,
              index
            );
          }
        };
        setTimeout(() => {
          const getSlotLineItemID = (slot: any, adId: string, ad: string) => {
            // Get slot information and line item ID. We need to convert the line item ID to string as we use string line item IDs in the config
            const responseInformation = slot.getResponseInformation();
            const lineItemID =
              responseInformation?.lineItemId?.toString() || responseInformation?.sourceAgnosticLineItemId?.toString();
            console.log(
              `[Platform] ${ad} Detected line item for ${adId} - ${lineItemID}, does the div of it exist on the page - ${Boolean(
                document.querySelector(`#${adId}`)
              )}, Is it included in whitelist - ${targets[
                ad
              ].replaceExistingAd?.publisher_gam_lineitems_whitelist?.includes(lineItemID)}`
            );
            return lineItemID;
          };
          const refreshAllAds = () => {
            const availableSlots: Set<any> = new Set();
            const adIdsExcludingVuukleAds = this.currentAdIds.filter(
              (item) => !item.includes('div-gpt-ad-1497448474263')
            );
            const getAndUpdateAvailableSlotList = () => {
              adIdsExcludingVuukleAds.forEach((adId) => {
                const slot = findSlotById(adId, windowContext);
                if (slot) {
                  availableSlots.add(slot);
                }
              });
            };
            getAndUpdateAvailableSlotList();
            // If there are no ads with the configured line item IDs on the page, then refresh the given ads every 30 seconds instead
            if (availableSlots.size > 0) {
              const pad = (num: number) => {
                return num.toString().padStart(2, '0');
              };
              setInterval(() => {
                getAndUpdateAvailableSlotList();
                const now = new Date();
                const time = `[${now.getHours().toString()}:${pad(now.getMinutes())}:${pad(now.getSeconds())}]`;
                console.log(`[Platform] ${time} Refresh all slots`);
                availableSlots.forEach((adSlot) => {
                  windowContext.googletag.pubads().refresh([adSlot]);
                });
              }, 30000);
            }
          };
          let isAllAdRefreshIntervalCreated = false;
          Object.keys(adReplacers).forEach(async (ad) => {
            /** If there are whitelisted GAM (Google Ad Manager) Line Items and those are not present on the page, we should not replace the ad */
            if (targets[ad].replaceExistingAd?.publisher_gam_lineitems_whitelist) {
              let whitelistedLineItemIsOnThePage = false;
              const availableSlots: any[] = [];
              const adsNeedToBeRendered: string[] = [];
              const adsThatAreRendered: string[] = [];
              let allAdsRendered = false;
              const adIdsExcludingVuukleAds = this.currentAdIds.filter(
                (item) => !item.includes('div-gpt-ad-1497448474263')
              );
              adIdsExcludingVuukleAds.forEach((adId: string, adIndex: number) => {
                const slot = findSlotById(adId, windowContext);
                if (slot) {
                  availableSlots.push(slot);
                  const lineItemID = getSlotLineItemID(slot, adId, ad);

                  if (
                    lineItemID &&
                    targets[ad].replaceExistingAd?.publisher_gam_lineitems_whitelist?.includes(lineItemID)
                  ) {
                    adsThatAreRendered.push(adId);
                    whitelistedLineItemIsOnThePage = true;
                    replaceAd(ad);
                  }

                  if ((lineItemID === undefined || lineItemID === 'undefined') && document.querySelector(`#${adId}`)) {
                    adsNeedToBeRendered.push(adId);
                    let slotRendered = false;
                    window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
                      if (event.slot === slot) {
                        if (!slotRendered) {
                          console.log(`[Platform] ${adId} is now rendered`);
                          const slotLineItemID = getSlotLineItemID(slot, adId, ad);
                          if (
                            slotLineItemID &&
                            targets[ad].replaceExistingAd?.publisher_gam_lineitems_whitelist?.includes(slotLineItemID)
                          ) {
                            whitelistedLineItemIsOnThePage = true;
                            replaceAd(ad);
                          }
                          adsThatAreRendered.push(adId);

                          if (adsNeedToBeRendered.length <= adsThatAreRendered.length) {
                            allAdsRendered = true;
                          }
                        }
                        slotRendered = true;
                      }
                    });
                  }

                  if (
                    adsNeedToBeRendered.length === adsThatAreRendered.length ||
                    (adsNeedToBeRendered.length === 0 && adIndex === adIdsExcludingVuukleAds.length - 1)
                  ) {
                    allAdsRendered = true;
                  }
                }
              });
              if (!isAllAdRefreshIntervalCreated) {
                const interval = setInterval(() => {
                  if (allAdsRendered && !whitelistedLineItemIsOnThePage) {
                    refreshAllAds();
                    clearInterval(interval);
                  }

                  if (allAdsRendered && whitelistedLineItemIsOnThePage) {
                    clearInterval(interval);
                  }
                }, 500);
                isAllAdRefreshIntervalCreated = true;
              }
            } else {
              replaceAd(ad);
            }
          });
        }, 30000);
      }
    }
  }

  /**
   * Removes every ad from the page
   */
  public static removeAllAds() {
    if (AdvertisingCreator.canCreateAds) {
      const allAds = document.querySelectorAll('.vuukle-ads');
      if (allAds.length === 0) {
        const interval = setInterval(() => {
          const ads = document.querySelectorAll('.vuukle-ads');
          if (ads.length > 0) {
            clearInterval(interval);
          }
          ads.forEach((ad) => {
            ad.remove();
          });
        }, 2000);
      } else {
        allAds.forEach((ad) => {
          ad.remove();
        });
      }
      AdvertisingCreator.canCreateAds = false;
    }
  }

  /**
   * Replace other AdSense ad units on the page with GPT units after 30 seconds
   * @param index
   */
  public findAndReplaceAdSenseUnits(index: string = '') {
    const gptNumbersForReplacingAnyAdSenseUnit: (string | undefined)[] = [];
    const gptNumbersForReplacingSpecificAdSenseUnit: { gptNumber?: string; slotID: string; targetName: string }[] = [];

    if (this.apiKeyConfig.targets) {
      Object.keys(this.apiKeyConfig.targets).forEach((key) => {
        let canReplaceAds = true;
        if (this.apiKeyConfig.targets?.[key].ignorePaths || this.apiKeyConfig.targets?.[key].ignorePathsMobile) {
          const ignorePaths =
            Session.isMobileUser && this.apiKeyConfig.targets?.[key].ignorePathsMobile
              ? this.apiKeyConfig.targets?.[key].ignorePathsMobile
              : this.apiKeyConfig.targets?.[key].ignorePaths || [];
          const doesPathMatch = matchesPath(ignorePaths as string[]);
          if (doesPathMatch) {
            return;
          }
        }

        if (typeof this.apiKeyConfig.targets?.[key].onlyDesktop === 'boolean') {
          const onMobileDesktopOnly = Session.isMobileUser && this.apiKeyConfig.targets?.[key].onlyDesktop;
          const onDesktopMobileOnly = !Session.isMobileUser && !this.apiKeyConfig.targets?.[key].onlyDesktop;
          canReplaceAds = !onMobileDesktopOnly && !onDesktopMobileOnly;
        }

        if (this.apiKeyConfig.targets?.[key].replaceAdSense && canReplaceAds) {
          const gptNumber: string | undefined = getTheGPTNumberOfTheAdTarget(key);
          gptNumbersForReplacingAnyAdSenseUnit.push(gptNumber);
        }

        if (
          (this.apiKeyConfig.targets?.[key].replaceAdSenseWithSlotID ||
            this.apiKeyConfig.targets?.[key].replaceAdSenseWithSlotIDMobile) &&
          canReplaceAds
        ) {
          const slotID =
            Session.isMobileUser && this.apiKeyConfig.targets?.[key].replaceAdSenseWithSlotIDMobile
              ? this.apiKeyConfig.targets?.[key].replaceAdSenseWithSlotIDMobile
              : this.apiKeyConfig.targets?.[key].replaceAdSenseWithSlotID;
          const gptNumber: string | undefined = getTheGPTNumberOfTheAdTarget(key);
          gptNumbersForReplacingSpecificAdSenseUnit.push({
            gptNumber,
            slotID: slotID || '',
            targetName: key,
          });
        }
      });
    }

    if (gptNumbersForReplacingAnyAdSenseUnit.length === 0 && gptNumbersForReplacingSpecificAdSenseUnit.length === 0) {
      return;
    }

    const adsenseDivs = document.querySelectorAll('ins[class="adsbygoogle"]');
    let placementElements: HTMLElement[] = [];
    if (adsenseDivs.length > 0) {
      adsenseDivs.forEach((adsenseDiv: HTMLElement) => {
        if (adsenseDiv) {
          placementElements.push(adsenseDiv);
        }
      });
    }
    setTimeout(() => {
      if (gptNumbersForReplacingSpecificAdSenseUnit.length > 0) {
        gptNumbersForReplacingSpecificAdSenseUnit.forEach((unit) => {
          const element = document.querySelector(`ins[data-ad-slot="${unit.slotID}"]`);
          if (element) {
            const adjacentDiv = document.createElement('div');
            element.insertAdjacentElement('afterend', adjacentDiv);
            element.parentElement?.removeChild(element);
            this.createGPTUnit(unit.targetName, adjacentDiv, undefined, index, unit.gptNumber);
          }
        });
      }

      placementElements.forEach((element) => {
        if (gptNumbersForReplacingAnyAdSenseUnit.length > 0) {
          emptyTheContentsOfAnElement(element);
          this.createGPTUnit(
            '',
            element as HTMLDivElement,
            undefined,
            index,
            gptNumbersForReplacingAnyAdSenseUnit.shift()
          );
        }
      });
    }, 30000);
  }
}

export default AdUnitCreator;
